import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Wizard from './components/Wizard/Wizard';
import PlanView from './components/PlanView';
import DinoGame from './components/Game/DinoGame';
import StravaConnectButton from './components/Auth/StravaConnectButton';
import StravaCallbackPage from './components/Auth/StravaCallbackPage';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import TopRightMenu from './components/common/TopRightMenu';
import SiteFooter from './components/common/SiteFooter';
import PlanHomeCard from './components/Home/PlanHomeCard';
import { API_BASE } from './config';
import { getPlanEndDateISO, getPlanWorkoutsCompletion, isPlanClosedStatus, isPlanEnded } from './utils/plan_progress';

import GpxDebug from './components/GpxDebug';
import StravaDebug from './components/StravaDebug';

import WorkoutDetails from './components/WorkoutDetails';
import LegalMarkdownPage from './components/Legal/LegalMarkdownPage';
import { getLegalDoc } from './components/Legal/legalTexts';
import LegalAcceptanceModal from './components/Legal/LegalAcceptanceModal';
import AdminActivityPage from './components/Admin/AdminActivityPage';
import UnsubscribePage from './components/Notifications/UnsubscribePage';

function Home() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMine = async () => {
      if (!user) {
        setPlans([]);
        setPlansError(null);
        setLoadingPlans(false);
        return;
      }

      setLoadingPlans(true);
      setPlansError(null);
      try {
        const listRes = await fetch(`${API_BASE}/plans?scope=mine`, { credentials: 'include' });
        const listJson = await listRes.json().catch(() => []);
        const list = Array.isArray(listJson) ? listJson : [];

        // Fetch full plan payload per plan to compute progress + last/next workouts.
        const details = await Promise.all(
          list.map(async (p) => {
            const res = await fetch(`${API_BASE}/plans/${p.id}`, { credentials: 'include' });
            const json = await res.json();
            return { ...json, _list: p };
          })
        );

        if (cancelled) return;
        setPlans(details.filter(Boolean));
      } catch (e) {
        if (cancelled) return;
        setPlans([]);
        setPlansError(String(e?.message || 'Failed to fetch plans'));
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    };

    fetchMine();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Avoid UI flicker (marketing hero) while we don't know auth state yet.
  if (authLoading) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-gray-500">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4" />
          <div className="text-sm font-semibold">Loading…</div>
        </div>
      </div>
    );
  }

  const hasPlans = Boolean(user) && Array.isArray(plans) && plans.length > 0;


  // Sort:
  // - first: non-ended plans (end date not passed), by completion % DESC
  // - then: ended plans (end date < today), by end date DESC (most recent first)
  const sortedPlans = (() => {
    const withMeta = (plans || []).map((p) => {
      const endISO = getPlanEndDateISO(p?.plan);
      const completion = getPlanWorkoutsCompletion(p?.plan);
      const ended = isPlanClosedStatus(p?.status) || isPlanEnded(endISO, new Date());
      return { ...p, _meta: { endISO, ended, completionPct: completion.pct } };
    });

    return withMeta.slice().sort((a, b) => {
      const aEnded = Boolean(a?._meta?.ended);
      const bEnded = Boolean(b?._meta?.ended);
      if (aEnded !== bEnded) return aEnded ? 1 : -1;

      if (!aEnded) {
        const aPct = Number(a?._meta?.completionPct || 0);
        const bPct = Number(b?._meta?.completionPct || 0);
        if (aPct !== bPct) return bPct - aPct;
        // Tie-breaker: earlier end date first (if available)
        const aEnd = String(a?._meta?.endISO || '');
        const bEnd = String(b?._meta?.endISO || '');
        return aEnd.localeCompare(bEnd);
      }

      // Ended: most recent end date first
      const aEnd = String(a?._meta?.endISO || '');
      const bEnd = String(b?._meta?.endISO || '');
      return bEnd.localeCompare(aEnd);
    });
  })();

  if (user) {
    return (
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">{t('home.your_plans_title')}</h1>
            <p className="text-gray-500 mt-2 text-lg font-medium">{t('home.your_plans_subtitle')}</p>
          </div>

          {hasPlans ? (
            <Link
              to="/wizard"
              className="inline-flex items-center justify-center bg-indigo-600 text-white font-bold py-2.5 px-6 rounded-full shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:bg-indigo-700 transition-all duration-200 text-sm whitespace-nowrap"
            >
              {t('plans.create_new')}
            </Link>
          ) : null}
        </div>

        {loadingPlans ? (
          <div className="p-12 bg-white rounded-2xl shadow-sm border border-gray-100 text-gray-400 flex flex-col items-center justify-center animate-pulse">
            <div className="h-10 w-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
            {t('plans.loading')}
          </div>
        ) : plansError ? (
          <div className="p-6 bg-red-50 rounded-2xl border border-red-100 text-red-700 font-medium">
            {t('plans.error')}: {plansError}
          </div>
        ) : hasPlans ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPlans.map((p) => (
              <PlanHomeCard key={p.id} planSummary={p} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200/60 shadow-xl shadow-gray-200/50 p-8 sm:p-12 text-center max-w-2xl mx-auto mt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('home.cta')}</h3>
            <p className="text-gray-500 mb-8">{t('home.subtitle')}</p>
            <div className="flex justify-center">
              <Link
                to="/wizard"
                className="inline-flex items-center justify-center bg-indigo-600 text-white font-bold py-3 px-8 rounded-full shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:bg-indigo-700 transition-all duration-200 text-base"
              >
                {t('home.cta')}
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Logged out: show the newcomer / marketing content.
  return (
    <div className="py-12 sm:py-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 rounded-3xl p-8 sm:p-16 text-white mb-16 shadow-2xl shadow-indigo-900/20 text-center sm:text-left relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none group-hover:scale-110 transition-transform duration-1000"></div>
        <div className="relative z-10">
          <h1 className="text-4xl sm:text-6xl font-black mb-6 tracking-tight leading-tight drop-shadow-sm">{t('home.title')}</h1>
          <p className="text-xl sm:text-2xl opacity-90 mb-10 font-medium max-w-2xl leading-relaxed text-indigo-50">
            {t('home.subtitle')}
          </p>
          <Link to="/wizard" className="inline-flex items-center justify-center bg-white text-indigo-600 font-bold py-4 px-10 rounded-full shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 text-lg sm:text-xl">
            {t('home.cta')}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-300 group">
          <div className="h-12 w-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h3 className="font-bold text-xl mb-3 text-gray-900 tracking-tight">{t('home.features.intake.title')}</h3>
          <p className="text-gray-500 leading-relaxed">{t('home.features.intake.desc')}</p>
        </div>
        <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-300 group">
          <div className="h-12 w-12 rounded-xl bg-green-50 text-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>
          <h3 className="font-bold text-xl mb-3 text-gray-900 tracking-tight">{t('home.features.safety.title')}</h3>
          <p className="text-gray-500 leading-relaxed">{t('home.features.safety.desc')}</p>
        </div>
        <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-300 group">
          <div className="h-12 w-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </div>
          <h3 className="font-bold text-xl mb-3 text-gray-900 tracking-tight">{t('home.features.adaptive.title')}</h3>
          <p className="text-gray-500 leading-relaxed">{t('home.features.adaptive.desc')}</p>
        </div>
      </div>
    </div>
  );
}

// Debug wrapper to center the game
function GameDebug() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-200">
            <div className="bg-white p-8 rounded-xl shadow-xl">
                <h1 className="text-center font-bold mb-4">Game Debug Mode</h1>
                <DinoGame />
            </div>
        </div>
    );
}

function App() {
  const { t, i18n } = useTranslation();
  const { loading: authLoading, user, displayName, logout, needsEmail, isRoot, impersonation, resetImpersonation, legal, refresh } = useAuth();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // Persist language choice across visits
    localStorage.setItem('runcoach_lang', lng);
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50/50 font-sans text-gray-900 antialiased selection:bg-indigo-100 selection:text-indigo-700 flex flex-col">
        <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50 transition-all duration-300">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="text-2xl font-black tracking-tighter bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent hover:opacity-80 transition-opacity">
                Jolkia
              </Link>
              <div className="flex items-center gap-3 md:gap-4">
                {authLoading ? (
                  <div className="animate-pulse h-8 w-8 bg-gray-200 rounded-full"></div>
                ) : null}

                {!authLoading && impersonation?.active ? (
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-200 bg-orange-50/50 text-orange-900 text-xs font-bold shadow-sm backdrop-blur-sm">
                    <span className="uppercase tracking-wider text-[10px] font-extrabold text-orange-600/80">Impersonating</span>
                    <span className="max-w-[220px] truncate">
                      {impersonation?.target_user?.displayName || impersonation?.target_user?.email || impersonation?.target_user?.id}
                    </span>
                    <button
                      type="button"
                      onClick={() => resetImpersonation().catch(() => {})}
                      className="ml-1 px-2 py-0.5 rounded-md bg-white border border-orange-200 text-orange-700 hover:bg-orange-100 text-[10px] font-bold transition-colors shadow-sm"
                      title="Stop impersonating"
                    >
                      Reset
                    </button>
                  </div>
                ) : null}

                {/* Optional quick connect CTA on larger screens */}
                {!authLoading && !user ? (
                  <StravaConnectButton className="hidden sm:inline-flex bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-2 px-4 rounded-full shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 hover:-translate-y-0.5 transition-all duration-300 text-sm items-center gap-2" />
                ) : null}

                <TopRightMenu
                  isLoggedIn={Boolean(user)}
                  displayName={displayName}
                  onLogout={logout}
                  onChangeLanguage={changeLanguage}
                  currentLanguage={i18n.language}
                  isRoot={Boolean(isRoot)}
                  impersonation={impersonation}
                />

                {/* Keep the email-completion link visible if needed */}
                {!authLoading && user && needsEmail ? (
                  <Link
                    to="/strava/callback"
                    className="hidden md:inline-flex text-xs font-bold px-3 py-1.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-full hover:bg-yellow-100 transition-colors"
                    title="Please add your email to complete signup"
                  >
                    Email needed
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        </nav>

        <main className="flex-1 w-full max-w-7xl mx-auto sm:px-6 lg:px-8 pb-8 md:pb-12 landscape:pb-4">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/wizard" element={<Wizard />} />
              <Route path="/plans/:planId" element={<PlanView />} />
              <Route path="/workouts/:workoutId" element={<WorkoutDetails />} />
              <Route path="/strava/callback" element={<StravaCallbackPage />} />
              <Route path="/admin" element={<AdminActivityPage />} />
              <Route path="/notifications/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/game-debug" element={<GameDebug />} />
              <Route path="/gpx-debug" element={<GpxDebug />} />
              <Route path="/strava-debug" element={<StravaDebug />} />
              <Route
                path="/terms"
                element={
                  <LegalMarkdownPage
                    title={t('legal.pages.cgu')}
                    markdown={getLegalDoc(i18n.language, 'terms')}
                  />
                }
              />
              <Route
                path="/terms/sales"
                element={
                  <LegalMarkdownPage
                    title={t('legal.pages.cgv')}
                    markdown={getLegalDoc(i18n.language, 'termsSales')}
                  />
                }
              />
              <Route
                path="/privacy"
                element={
                  <LegalMarkdownPage
                    title={t('legal.pages.privacy')}
                    markdown={getLegalDoc(i18n.language, 'privacy')}
                  />
                }
              />
              <Route
                path="/ai-data-usage"
                element={
                  <LegalMarkdownPage
                    title={t('legal.pages.ai_data_usage')}
                    markdown={getLegalDoc(i18n.language, 'aiDataUsage')}
                  />
                }
              />
            </Routes>
        </main>

        <div className="hidden md:block mt-auto">
          <SiteFooter />
        </div>

        <LegalAcceptanceGate
          authLoading={authLoading}
          user={user}
          legal={legal}
          onAccepted={async () => refresh()}
          onLogout={logout}
        />
        <EmailRequiredGate
          authLoading={authLoading}
          user={user}
          needsEmail={needsEmail}
        />
      </div>
    </Router>
  );
}

function EmailRequiredGate({ authLoading, user, needsEmail }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (!needsEmail) return;

    // Allow callback page
    if (location.pathname === '/strava/callback') return;

    // Redirect to callback page
    navigate('/strava/callback', { replace: true });
  }, [authLoading, user, needsEmail, location.pathname, navigate]);

  return null;
}

function LegalAcceptanceGate({ authLoading, user, legal, onAccepted, onLogout }) {
  const location = useLocation();
  const pathname = location?.pathname || '';
  const isLegalPage =
    pathname === '/terms' ||
    pathname === '/terms/sales' ||
    pathname === '/privacy' ||
    pathname === '/ai-data-usage';

  return (
    <LegalAcceptanceModal
      open={!isLegalPage && !authLoading && Boolean(user) && Boolean(legal?.needs_acceptance)}
      legal={legal}
      onAccepted={onAccepted}
      onLogout={onLogout}
    />
  );
}

export default function AppWithProviders() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
